// src/pages/partner/PartnerValuationRequestDetail.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";

function fmtDate(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return String(d);
  }
}

function badgeClass(status) {
  const s = (status || "").toLowerCase();
  if (s === "pending" || s === "assigned") return "bg-amber-100 text-amber-800 border-amber-200";
  if (s === "in_progress") return "bg-blue-100 text-blue-800 border-blue-200";
  if (s === "completed") return "bg-green-100 text-green-800 border-green-200";
  if (s === "cancelled") return "bg-gray-100 text-gray-700 border-gray-200";
  return "bg-gray-100 text-gray-700 border-gray-200";
}

function statusLabel(status) {
  const labels = {
    pending: "En attente",
    assigned: "En attente",
    in_progress: "En cours",
    completed: "Terminée",
    cancelled: "Annulée",
  };
  return labels[status] || status;
}

export default function PartnerValuationRequestDetail() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [request, setRequest] = useState(null);
  const [artwork, setArtwork] = useState(null);

  const [successMessage, setSuccessMessage] = useState("");
  const [actionInProgress, setActionInProgress] = useState("");

  const [expertMessage, setExpertMessage] = useState("");
  const [estimatedValue, setEstimatedValue] = useState("");
  const [valueCurrency, setValueCurrency] = useState("EUR");

  const load = async () => {
    try {
      setLoading(true);
      setErr("");

      const { data: reqData, error: reqError } = await supabase
        .from("partner_requests")
        .select(`
          *,
          client:client_id (
            first_name,
            last_name,
            email,
            company
          )
        `)
        .eq("id", id)
        .single();

      if (reqError) throw reqError;
      setRequest(reqData);

      if (reqData.artwork_id) {
        const { data: artworkData } = await supabase
          .from("artworks")
          .select("*")
          .eq("id", reqData.artwork_id)
          .maybeSingle();
        setArtwork(artworkData || null);
      }

      const answer = reqData.payload?.answer || {};
      setExpertMessage(answer.message || "");
      setEstimatedValue(answer.value ? String(answer.value) : "");
      setValueCurrency(answer.currency || "EUR");
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Erreur chargement demande");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(""), 4000);
  };

  const updateStatus = async (newStatus, action, successMsg) => {
    try {
      setSaving(true);
      setActionInProgress(action);
      setErr("");

      const { error } = await supabase
        .from("partner_requests")
        .update({ status: newStatus })
        .eq("id", id);

      if (error) throw error;

      await load();
      showSuccess(successMsg);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Impossible de mettre à jour le statut");
    } finally {
      setSaving(false);
      setActionInProgress("");
    }
  };

  const saveValuation = async () => {
    try {
      setSaving(true);
      setErr("");

      const value = parseFloat(estimatedValue);
      if (isNaN(value) || value <= 0) {
        alert("Veuillez entrer une valeur estimée valide");
        return;
      }

      const nextPayload = {
        ...(request.payload || {}),
        answer: { value, currency: valueCurrency, message: expertMessage },
      };

      const { error } = await supabase
        .from("partner_requests")
        .update({
          payload: nextPayload,
          status: "completed",
          done_at: new Date().toISOString(),
        })
        .eq("id", id);

      if (error) throw error;

      await load();
      showSuccess("✓ Estimation enregistrée avec succès");
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Impossible d'enregistrer l'estimation");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Chargement...</div>
      </div>
    );
  }

  if (err && !request) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
            {err}
          </div>
          <button
            onClick={() => navigate("/partner/valuation")}
            className="mt-4 text-sm text-gray-600 hover:text-gray-900"
          >
            ← Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  const payload = request?.payload || {};

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <button
            onClick={() => navigate("/partner/valuation")}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Retour à mes demandes
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-serif font-bold mb-2">Demande d'estimation</h1>
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <span>📅 {fmtDate(request.created_at)}</span>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold border ${badgeClass(
                    request.status
                  )}`}
                >
                  {statusLabel(request.status)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {successMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-green-900">{successMessage}</p>
          </div>
        )}

        {err && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-red-900">{err}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold mb-4">Image de l'œuvre</h2>
              <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                {artwork?.image_url ? (
                  <img
                    src={artwork.image_url}
                    alt={artwork.title}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <span className="text-6xl">🖼️</span>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold mb-4">Informations détaillées</h2>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase mb-1">Titre</p>
                  <p className="font-semibold">{artwork?.title || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase mb-1">Artiste</p>
                  <p className="font-semibold">{artwork?.artist || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase mb-1">Année de création</p>
                  <p className="font-semibold">{artwork?.year || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase mb-1">Technique</p>
                  <p className="font-semibold">{artwork?.medium || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase mb-1">Dimensions</p>
                  <p className="font-semibold">
                    {artwork?.width && artwork?.height
                      ? `${artwork.width} × ${artwork.height} ${artwork.dimensions_unit || "cm"}`
                      : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase mb-1">Signature</p>
                  <p className="font-semibold">{artwork?.signature_info || artwork?.signature_desc || (artwork?.signature ? "Oui" : "—")}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase mb-1">Certificat</p>
                  <p className="font-semibold">{artwork?.certificate_authenticity ? "Oui" : "Non"}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase mb-1">État</p>
                  <p className="font-semibold">{artwork?.condition || "—"}</p>
                </div>
              </div>

              {artwork?.provenance && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500 uppercase mb-1">Provenance</p>
                  <p className="text-sm">{artwork.provenance}</p>
                </div>
              )}

              {artwork?.description && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500 uppercase mb-1">Description</p>
                  <p className="text-sm">{artwork.description}</p>
                </div>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-xs text-blue-600 uppercase font-semibold mb-2">
                Message du collectionneur
              </p>
              {payload.purpose && (
                <p className="text-sm text-blue-900 font-medium mb-1">Objectif : {payload.purpose}</p>
              )}
              <p className="text-sm text-blue-900">{request.notes || payload.details || "—"}</p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold mb-4">Collectionneur</h3>
              <div className="space-y-2 text-sm">
                <p>
                  <strong>Nom :</strong> {request.client?.first_name} {request.client?.last_name}
                </p>
                {request.client?.company && (
                  <p>
                    <strong>Entreprise :</strong> {request.client.company}
                  </p>
                )}
                <p>
                  <strong>Email :</strong> {request.client?.email}
                </p>
              </div>
            </div>

            {(request.status === "pending" || request.status === "assigned") && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold mb-4">Actions rapides</h3>
                <button
                  onClick={() =>
                    updateStatus("in_progress", "start", "✓ Demande marquée comme en cours")
                  }
                  disabled={saving}
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {actionInProgress === "start" ? "Traitement..." : "🔍 Commencer l'estimation"}
                </button>
              </div>
            )}

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold mb-4">Votre estimation</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Valeur estimée *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={estimatedValue}
                      onChange={(e) => setEstimatedValue(e.target.value)}
                      placeholder="0"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      disabled={request.status === "completed"}
                    />
                    <select
                      value={valueCurrency}
                      onChange={(e) => setValueCurrency(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      disabled={request.status === "completed"}
                    >
                      <option value="EUR">EUR</option>
                      <option value="USD">USD</option>
                      <option value="GBP">GBP</option>
                      <option value="CHF">CHF</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes d'expertise
                  </label>
                  <textarea
                    value={expertMessage}
                    onChange={(e) => setExpertMessage(e.target.value)}
                    rows={6}
                    placeholder="Justification de l'estimation, comparables, état du marché..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    disabled={request.status === "completed"}
                  />
                </div>

                {request.status !== "completed" && (
                  <button
                    onClick={saveValuation}
                    disabled={saving || !estimatedValue}
                    className="w-full px-4 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {saving ? "Enregistrement..." : "✅ Enregistrer l'estimation"}
                  </button>
                )}

                {request.status === "completed" && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-sm font-semibold text-green-900">
                      ✓ Estimation terminée le {fmtDate(request.done_at)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
